import React, { useEffect } from 'react';
import { CarehomeAddressType } from '@typings/CarehomeInfo';
import { v4 as uuidv4 } from 'uuid';

interface CarehomeAddressProps {
  isDisabled?: boolean;
  notShowInitialAddress?: boolean;
  savedCarehomeAddressFromTeams?: CarehomeAddressType | null;
  carehomeAddress: CarehomeAddressType;
  setCarehomeAddress: (address: CarehomeAddressType) => void;
}

const CarehomeAddress: React.FC<CarehomeAddressProps> = ({
  isDisabled,
  carehomeAddress,
  setCarehomeAddress,
  savedCarehomeAddressFromTeams,
  notShowInitialAddress,
}) => {
  useEffect(() => {
    if (!notShowInitialAddress && savedCarehomeAddressFromTeams) {
      setCarehomeAddress(savedCarehomeAddressFromTeams);
    }
  }, [
    savedCarehomeAddressFromTeams,
    notShowInitialAddress,
    setCarehomeAddress,
  ]);

  const componentId = uuidv4();

  return (
    <div className="carehome-address">
      <div className="govuk-form-group">
        <label
          className="govuk-label"
          htmlFor={`address-line-1-${componentId}`}
        >
          Address line 1
        </label>
        <input
          className="govuk-input"
          id={`address-line-1-${componentId}`}
          name="addressLine1"
          type="text"
          autoComplete="addressLine1"
          value={carehomeAddress.addressLine1 ?? ''}
          disabled={isDisabled}
          readOnly={isDisabled}
          onChange={e =>
            setCarehomeAddress({
              ...carehomeAddress,
              addressLine1: e.target.value,
            })
          }
        />
      </div>
      <div className="govuk-form-group">
        <label
          className="govuk-label"
          htmlFor={`address-line-2-${componentId}`}
        >
          Address line 2 (optional)
        </label>
        <input
          className="govuk-input"
          id={`address-line-2-${componentId}`}
          name="addressLine2"
          type="text"
          autoComplete="address-line2"
          value={carehomeAddress.addressLine2 ?? ''}
          disabled={isDisabled}
          readOnly={isDisabled}
          onChange={e =>
            setCarehomeAddress({
              ...carehomeAddress,
              addressLine2: e.target.value,
            })
          }
        />
      </div>
      <div className="govuk-form-group">
        <label className="govuk-label" htmlFor={`address-town-${componentId}`}>
          Town or city
        </label>
        <input
          className="govuk-input govuk-!-width-two-thirds"
          id={`address-town-${componentId}`}
          name="addressTown"
          type="text"
          autoComplete="address-level2"
          value={carehomeAddress.city ?? ''}
          disabled={isDisabled}
          readOnly={isDisabled}
          onChange={e =>
            setCarehomeAddress({
              ...carehomeAddress,
              city: e.target.value,
            })
          }
        />
      </div>
      <div className="govuk-form-group">
        <label
          className="govuk-label"
          htmlFor={`address-county-${componentId}`}
        >
          County (optional)
        </label>
        <input
          className="govuk-input govuk-!-width-two-thirds"
          id={`address-county-${componentId}`}
          name="addressCounty"
          type="text"
          value={carehomeAddress.county ?? ''}
          disabled={isDisabled}
          readOnly={isDisabled}
          onChange={e =>
            setCarehomeAddress({
              ...carehomeAddress,
              county: e.target.value,
            })
          }
        />
      </div>
      <div className="govuk-form-group">
        <label
          className="govuk-label"
          htmlFor={`address-postcode-${componentId}`}
        >
          Postcode
        </label>
        <input
          className="govuk-input govuk-input--width-10"
          id={`address-postcode-${componentId}`}
          name="addressPostcode"
          type="text"
          autoComplete="postal-code"
          disabled={isDisabled}
          readOnly={isDisabled}
          value={carehomeAddress.postCode ?? ''}
          onChange={e =>
            setCarehomeAddress({
              ...carehomeAddress,
              postCode: e.target.value,
            })
          }
        />
      </div>
    </div>
  );
};

CarehomeAddress.defaultProps = {
  isDisabled: false,
  notShowInitialAddress: false,
  savedCarehomeAddressFromTeams: null,
};

export default CarehomeAddress;
